import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Play } from 'lucide-react';
import { diagnosticsAPI } from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';

/**
 * Receivables Overlap diagnostic card.
 *
 * Calls GET /admin/diagnostics/receivables-overlap and displays per-entity
 * booking/invoice dedup analysis: how many bookings are suppressed by
 * finalized invoices, overlap amounts removed, and top amount deltas.
 */
export function ReceivablesOverlapPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expandedEntity, setExpandedEntity] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await diagnosticsAPI.receivablesOverlap();
      setData(d);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => {
    const n = Number(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const entitiesWithActivity = (data?.entities ?? []).filter(
    (e) =>
      e.bookings_before_dedup.count > 0 ||
      e.invoices_receivable.count > 0
  );

  return (
    <Card data-testid="receivables-overlap-panel">
      <CardHeader>
        <CardTitle className="text-base">Receivables Overlap</CardTitle>
        <CardDescription>
          Per-entity booking/invoice dedup analysis (non-br_ltd entities)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button
          variant="outline"
          size="sm"
          onClick={run}
          disabled={loading}
          data-testid="run-overlap-btn"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Play className="h-4 w-4 mr-1" />
          )}
          Run Overlap Check
        </Button>

        {error && (
          <div className="text-sm text-red-600">{error}</div>
        )}

        {data && (
          <div className="space-y-4">
            {entitiesWithActivity.length === 0 ? (
              <p className="text-xs text-muted-foreground">No entities have receivable bookings or invoices.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="overlap-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1 pr-3 font-medium">Entity</th>
                      <th className="py-1 pr-3 font-medium text-right">Bookings (pre)</th>
                      <th className="py-1 pr-3 font-medium text-right">Bookings (post)</th>
                      <th className="py-1 pr-3 font-medium text-right">Matched</th>
                      <th className="py-1 pr-3 font-medium text-right">Overlap Removed</th>
                      <th className="py-1 pr-3 font-medium text-right">Invoices (receivable)</th>
                      <th className="py-1 pr-3 font-medium text-right">Drafts</th>
                      <th className="py-1 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {entitiesWithActivity.map((e) => (
                      <React.Fragment key={e.entity_id}>
                        <tr
                          className="border-b last:border-0 cursor-pointer hover:bg-muted/50"
                          onClick={() =>
                            setExpandedEntity(
                              expandedEntity === e.entity_id ? null : e.entity_id
                            )
                          }
                        >
                          <td className="py-1 pr-3">{e.entity_name}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.bookings_before_dedup.count}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.bookings_after_dedup.count}</td>
                          <td className="py-1 pr-3 text-right font-mono">
                            <Badge
                              variant={e.matched_pairs > 0 ? 'default' : 'outline'}
                              className="text-[10px]"
                            >
                              {e.matched_pairs}
                            </Badge>
                          </td>
                          <td className="py-1 pr-3 text-right font-mono">{fmt(e.overlap_removed)}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.invoices_receivable.count}</td>
                          <td className="py-1 pr-3 text-right font-mono">{e.draft_invoices_excluded}</td>
                          <td className="py-1 text-right text-muted-foreground">
                            {expandedEntity === e.entity_id ? '\u25B2' : '\u25BC'}
                          </td>
                        </tr>
                        {expandedEntity === e.entity_id && (
                          <tr>
                            <td colSpan={8} className="py-2 px-4 bg-muted/30">
                              <div className="space-y-2 text-xs">
                                <div className="grid grid-cols-2 gap-x-8 gap-y-1">
                                  <span className="text-muted-foreground">Bookings sum (pre-dedup):</span>
                                  <span className="font-mono">{fmt(e.bookings_before_dedup.sum)}</span>
                                  <span className="text-muted-foreground">Bookings sum (post-dedup):</span>
                                  <span className="font-mono">{fmt(e.bookings_after_dedup.sum)}</span>
                                  <span className="text-muted-foreground">Invoices receivable sum:</span>
                                  <span className="font-mono">{fmt(e.invoices_receivable.sum)}</span>
                                  <span className="text-muted-foreground">Unparseable external_ref:</span>
                                  <span className="font-mono">{e.unparseable_external_ref}</span>
                                </div>
                                {e.top_amount_deltas.length > 0 && (
                                  <div>
                                    <p className="font-medium mb-1">Top amount deltas (invoice - booking):</p>
                                    <table className="w-full">
                                      <thead>
                                        <tr className="text-left text-muted-foreground">
                                          <th className="pr-3">Uplisting ID</th>
                                          <th className="pr-3 text-right">Booking</th>
                                          <th className="pr-3 text-right">Invoice</th>
                                          <th className="text-right">Delta</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {e.top_amount_deltas.map((d, i) => (
                                          <tr key={i}>
                                            <td className="pr-3 font-mono">{d.uplisting_id}</td>
                                            <td className="pr-3 text-right font-mono">{fmt(d.booking_amount)}</td>
                                            <td className="pr-3 text-right font-mono">{fmt(d.invoice_amount)}</td>
                                            <td className={`text-right font-mono ${Number(d.delta) < 0 ? 'text-red-600' : ''}`}>
                                              {fmt(d.delta)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                                {e.top_amount_deltas.length === 0 && e.matched_pairs > 0 && (
                                  <p className="text-muted-foreground">All matched pairs have identical amounts.</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground">
              Generated: {data.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
